"use client";

import { cn } from "../lib/utils";
import { ScrollArea } from "./scroll-area";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Skeleton } from "./skeleton";
import { useState, useEffect } from "react";

// Cast SyntaxHighlighter to any to avoid TypeScript errors
const SyntaxHighlighterComponent = SyntaxHighlighter as any;

interface MarkdownRendererProps {
  content: string;
  isLoading?: boolean;
  className?: string;
  skipChartExtraction?: boolean;
}

export function MarkdownRenderer({ 
  content, 
  isLoading = false, 
  className,
  skipChartExtraction = false
}: MarkdownRendererProps) {
  const [processedContent, setProcessedContent] = useState<string>(content);
  
  // Process chart blocks on mount and when content changes
  useEffect(() => {
    if (!content || skipChartExtraction) {
      // If skipChartExtraction is true, don't process chart blocks
      setProcessedContent(content);
      return;
    }
    
    // Look for chart code blocks and extract the JSON
    const chartBlockRegex = /```chart\s*([\s\S]*?)```/g;
    let newContent = content;
    
    // Remove all chart blocks from the content
    if (chartBlockRegex.test(content)) {
      newContent = content.replace(chartBlockRegex, '');
      console.log("MARKDOWN: Removed chart code blocks from content");
    }
    
    // Look for table code blocks and extract the JSON
    const tableBlockRegex = /```table\s*([\s\S]*?)```/g;
    
    // Remove all table blocks from the content
    if (tableBlockRegex.test(newContent)) {
      newContent = newContent.replace(tableBlockRegex, '');
      console.log("MARKDOWN: Removed table code blocks from content");
    }
    
    setProcessedContent(newContent);
  }, [content, skipChartExtraction]);

  if (isLoading) {
    return (
      <div className={cn("space-y-2", className)}>
        <Skeleton className="h-4 w-[80%]" />
        <Skeleton className="h-4 w-[90%]" />
        <Skeleton className="h-4 w-[60%]" />
        <Skeleton className="h-4 w-[70%]" />
      </div>
    );
  }

  return (
    <ScrollArea className={cn("rounded-md overflow-auto", className)}>
      <div className="prose prose-sm dark:prose-invert max-w-none">
        {/* Markdown content */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({className, children, node, ...props}) {
              const match = /language-(\w+)/.exec(className || "");
              
              // Check if this is an inline code block
              // @ts-ignore - node.position exists but TypeScript doesn't know about it
              const isInlineCode = !node?.position?.start.line || 
                              // @ts-ignore
                              node.position.start.line === node.position.end.line;
              
              // Skip rendering chart code blocks - these should be handled by the parent component
              if (!isInlineCode && match && match[1] === "chart") {
                return null; // Don't render anything for chart blocks
              }
              
              // Skip rendering table code blocks - these should be handled by the parent component
              if (!isInlineCode && match && match[1] === "table") {
                return null; // Don't render anything for table blocks
              }
              
              return !isInlineCode && match ? (
                <SyntaxHighlighterComponent
                  language={match[1]}
                  PreTag="div"
                  className="rounded-md my-2"
                  style={vscDarkPlus as any}
                  // @ts-ignore - Ignoring TypeScript error for SyntaxHighlighter
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighterComponent>
              ) : (
                <code
                  className={cn("bg-muted px-1.5 py-0.5 rounded-md text-sm font-mono", className)}
                  {...props}
                >
                  {children}
                </code>
              );
            },
            // Customize list rendering
            ul({className, children, ...props}) {
              return (
                <ul className="list-disc pl-6 my-4 space-y-2" {...props}>
                  {children}
                </ul>
              );
            },
            ol({className, children, ...props}) {
              return (
                <ol className="list-decimal pl-6 my-4 space-y-2" {...props}>
                  {children}
                </ol>
              );
            },
            li({className, children, ...props}) {
              return (
                <li className="pl-1 my-1" {...props}>
                  {children}
                </li>
              );
            },
            // Customize paragraph spacing
            p({className, children, ...props}) {
              return (
                <p className="my-3 leading-relaxed" {...props}>
                  {children}
                </p>
              );
            },
            // Customize headings
            h1({className, children, ...props}) {
              return (
                <h1 className="text-2xl font-bold tracking-tight mt-6 mb-4" {...props}>
                  {children}
                </h1>
              );
            },
            h2({className, children, ...props}) {
              return (
                <h2 className="text-xl font-semibold tracking-tight mt-5 mb-3" {...props}>
                  {children}
                </h2>
              );
            },
            h3({className, children, ...props}) {
              return (
                <h3 className="text-lg font-medium mt-4 mb-2" {...props}>
                  {children}
                </h3>
              );
            },
            // Handle blockquotes
            blockquote({className, children, ...props}) {
              return (
                <blockquote className="border-l-4 border-muted-foreground/20 pl-4 py-1 my-4 italic" {...props}>
                  {children}
                </blockquote>
              );
            },
            // Tables
            table({className, children, ...props}) {
              return (
                <div className="overflow-x-auto my-4 rounded-md border">
                  <table className="min-w-full divide-y divide-border" {...props}>
                    {children}
                  </table>
                </div>
              );
            },
            tr({className, children, ...props}) {
              return (
                <tr className="border-b border-border last:border-0" {...props}>
                  {children}
                </tr>
              );
            },
            th({className, children, ...props}) {
              return (
                <th className="px-4 py-2 text-left text-sm font-medium bg-muted" {...props}>
                  {children}
                </th>
              );
            },
            td({className, children, ...props}) {
              return (
                <td className="px-4 py-2 text-sm" {...props}>
                  {children}
                </td>
              );
            },
            img({className, ...props}) {
              return (
                <img
                  className={cn("rounded-md max-w-full h-auto my-4", className)}
                  {...props}
                  loading="lazy"
                />
              );
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    </ScrollArea>
  );
} 